import { useRef, useState } from "preact/hooks"
import { CircleAlert, CircleCheck, Loader2, MinusCircle } from "lucide-react"
import { api_fetch, CheckDetail } from "@/api"
import {
  applyCashApplication,
  LedgerAccountJson,
  listAccountingInvoices,
  listLedgerAccounts,
  listQbCustomers,
  prepareCashApplication,
  QbCustomer,
} from "@/api/accounting.tsx"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useAsyncEffect } from "@/utils"
import { DepositAccountField } from "@/accounting/DepositAccountField.tsx"
import { find_auto_assigned_qb_customer } from "@/accounting/cash_application_display.ts"
import { bulk_apply_counts, BulkApplyRow, plan_bulk_apply } from "@/accounting/bulk_apply.ts"
import { check_api_path, check_detail_path } from "./check_path"

interface BulkApplyDialogProps {
  open: boolean
  on_close: () => void
  /** The checks to try, in the order to apply them. Already-applied checks should be left out. */
  checks: CheckDetail[]
  /** Display name of the connected accounting system ("Xero"). */
  accounting_system: string
  /** Called for each check as soon as its payment exists, so the list can show it applied. */
  on_applied: (check_number: string, payment_remote_id: string | undefined) => void
}

/** What happened to one check: applied, left for a person (`skipped`), or refused by the system. */
interface BulkApplyOutcome {
  status: "applied" | "skipped" | "failed"
  message: string
  payment_remote_id?: string
}

/**
 * Apply one check with every default left alone, or say why a person has to. The same calls the
 * Apply Cash page makes, in the same order: the check, its preview, the matched customer's
 * invoices, then the apply. `plan_bulk_apply` is the only place that decides.
 */
async function apply_one_check(check: CheckDetail, customers: QbCustomer[], deposit_account_remote_id: string): Promise<BulkApplyOutcome> {
  const detail_result = await api_fetch<CheckDetail>(check_api_path(check.number))
  if (!detail_result.ok || !detail_result.value.data.id) {
    return { status: "failed", message: "Could not load this check." }
  }
  const detail = detail_result.value.data
  const preview_result = await prepareCashApplication(detail.id!)
  if (!preview_result.ok) {
    return { status: "failed", message: preview_result.error.message || "Could not prepare this check." }
  }
  const preview = preview_result.value.data
  const customer = find_auto_assigned_qb_customer(
    {
      customer_name_hint: preview.customer_name || detail.customer_name,
      deduction_source: detail.source,
      customer_remote_id: preview.customer_remote_id,
    },
    customers
  )
  const invoices_result = customer ? await listAccountingInvoices(customer.remote_id, preview.check_number || check.number) : null
  if (invoices_result && !invoices_result.ok) {
    return { status: "failed", message: "Could not load this customer's invoices." }
  }
  const plan = plan_bulk_apply({
    preview,
    customer,
    invoices: invoices_result?.ok ? invoices_result.value.data : [],
    deposit_account_remote_id,
  })
  if (!plan.ready) {
    return { status: "skipped", message: plan.reason }
  }
  const result = await applyCashApplication(plan.request)
  if (!result.ok) {
    return { status: "failed", message: result.error.message || "The apply failed." }
  }
  const data = result.value.data
  if (!data.payment_created) {
    return { status: "failed", message: data.error ?? "The credit memo was created but the payment was not. Open the check to retry." }
  }
  return { status: "applied", message: data.warning ?? "", payment_remote_id: data.payment_remote_id }
}

function StatusIcon({ status }: { status: BulkApplyRow["status"] }) {
  if (status === "applied") return <CircleCheck className="h-4 w-4 text-green-600 shrink-0" />
  if (status === "failed") return <CircleAlert className="h-4 w-4 text-red-600 shrink-0" />
  if (status === "skipped") return <MinusCircle className="h-4 w-4 text-amber-600 shrink-0" />
  if (status === "running") return <Loader2 className="h-4 w-4 animate-spin text-slate-500 shrink-0" />
  return <span class="h-4 w-4 shrink-0" />
}

/**
 * Apply every ready check in one run. Each check goes through the same preview and apply as the
 * Apply Cash page, one at a time; a check that needs a decision (an unmatched invoice, a short
 * pay, uncoded lines) is skipped with its reason and a link, and nothing is assumed for it.
 */
export function BulkApplyDialog({ open, on_close, checks, accounting_system, on_applied }: BulkApplyDialogProps) {
  const [accounts, setAccounts] = useState<LedgerAccountJson[]>([])
  const [customers, setCustomers] = useState<QbCustomer[]>([])
  const [depositAccountId, setDepositAccountId] = useState<string | null>(null)
  const [rows, setRows] = useState<BulkApplyRow[]>([])
  const [running, setRunning] = useState(false)
  const [load_error, setLoadError] = useState<string | null>(null)
  const stop_requested = useRef(false)

  useAsyncEffect(async () => {
    if (!open) {
      return
    }
    setRows([])
    setLoadError(null)
    const [accounts_result, customers_result] = await Promise.all([listLedgerAccounts(true), listQbCustomers()])
    if (!accounts_result.ok || !customers_result.ok) {
      setLoadError(`Could not load ${accounting_system} accounts and customers.`)
      return
    }
    setAccounts(accounts_result.value.data)
    setCustomers(customers_result.value.data)
    const default_account = accounts_result.value.data.find((a: LedgerAccountJson) => a.is_default_for_payments)
    setDepositAccountId(default_account?.id ?? null)
  }, [open])

  function set_row(check_number: string, patch: Partial<BulkApplyRow>) {
    setRows((prev: BulkApplyRow[]) => prev.map((row: BulkApplyRow) => (row.check_number === check_number ? { ...row, ...patch } : row)))
  }

  async function handle_start() {
    const deposit_account = accounts.find((a: LedgerAccountJson) => a.id === depositAccountId)
    if (!deposit_account) {
      return
    }
    stop_requested.current = false
    setRunning(true)
    setRows(checks.map((check: CheckDetail) => ({ check_number: check.number, status: "queued", message: "" })))
    for (const check of checks) {
      if (stop_requested.current) {
        break
      }
      set_row(check.number, { status: "running" })
      const outcome = await apply_one_check(check, customers, deposit_account.remote_id)
      set_row(check.number, { status: outcome.status, message: outcome.message })
      if (outcome.status === "applied") {
        on_applied(check.number, outcome.payment_remote_id)
      }
    }
    setRunning(false)
  }

  function handle_default_change(accountId: string) {
    setAccounts((prev: LedgerAccountJson[]) => prev.map((a: LedgerAccountJson) => ({ ...a, is_default_for_payments: a.id === accountId })))
  }

  const started = rows.length > 0
  const counts = bulk_apply_counts(rows)
  const finished = started && !running
  // Rows that were never reached because the run was stopped.
  const not_run = finished ? counts.remaining : 0

  return (
    <Dialog open={open} onOpenChange={(o: boolean) => !o && !running && on_close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Apply ready checks</DialogTitle>
          <DialogDescription>
            Posts a payment in {accounting_system} for each of the {checks.length} unapplied check{checks.length === 1 ? "" : "s"} in this
            list that needs no decision: the customer and every invoice match, every line is coded, and the invoices
            equal the cash plus the credit memo. Anything else is skipped with the reason, for you to open and apply
            yourself. Each payment is dated its check date.
          </DialogDescription>
        </DialogHeader>

        {load_error ? <p class="text-sm text-red-600">{load_error}</p> : null}

        {!started ? (
          <DepositAccountField
            accounts={accounts}
            value={depositAccountId}
            onChange={setDepositAccountId}
            onDefaultChange={handle_default_change}
          />
        ) : (
          <div class="flex-1 min-h-0 flex flex-col gap-2">
            <div class="text-sm text-slate-700">
              {counts.applied} applied · {counts.skipped} skipped · {counts.failed} failed
              {running ? ` · ${counts.remaining} to go` : not_run > 0 ? ` · ${not_run} not run` : ""}
            </div>
            <div class="overflow-auto border border-gray-200 rounded-md divide-y divide-gray-100">
              {rows.map((row: BulkApplyRow) => (
                <div key={row.check_number} class="flex items-start gap-2 px-3 py-2 text-sm">
                  <StatusIcon status={row.status} />
                  <a
                    href={check_detail_path(row.check_number)}
                    target="_blank"
                    rel="noreferrer"
                    class="font-medium tabular-nums text-slate-900 hover:underline shrink-0">
                    {row.check_number}
                  </a>
                  <span class="text-slate-600 min-w-0">{row.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {running ? (
            <Button variant="outline" onClick={() => (stop_requested.current = true)}>
              Stop after this check
            </Button>
          ) : (
            <Button variant="outline" onClick={on_close}>
              {finished ? "Close" : "Cancel"}
            </Button>
          )}
          {!started ? (
            <Button disabled={!depositAccountId || checks.length === 0 || load_error !== null} onClick={handle_start}>
              Apply {checks.length} check{checks.length === 1 ? "" : "s"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
